import ContentCard from './ContentCard';
import FieldCopyButton from './FieldCopyButton';
import './CardGrid.css';

interface CardItem {
  id: string;
  content: string;
  label?: string;
}

interface ContextField {
  label: string;
  value: string;
}

interface CardGridProps {
  cards?: CardItem[];
  selectedIds?: Set<string>;
  onSelect?: (id: string) => void;
  onEdit?: (id: string, newContent: string) => void;
  onRegenerate?: (id: string, instructions?: string) => void;
  regeneratingId?: string | null;
  /** Card ids with a stored pre-regeneration value; shows the Go Back control. */
  undoableIds?: Set<string>;
  onUndo?: (id: string) => void;
  showCheckboxes?: boolean;
  requiredSelections?: number;
  contextFields?: ContextField[];
  compact?: boolean;
  staleIds?: Set<string>;
}

export default function CardGrid({
  cards = [],
  selectedIds = new Set(),
  onSelect,
  onEdit,
  onRegenerate,
  regeneratingId = null,
  undoableIds,
  onUndo,
  showCheckboxes = true,
  requiredSelections,
  contextFields = [],
  compact = false,
  staleIds = new Set(),
}: CardGridProps) {
  return (
    <div className="card-grid-wrapper">
      {/* Context fields shown above the grid */}
      {contextFields.length > 0 && (
        <div className="card-context">
          {contextFields.map((field, i) => (
            <div key={i} className="context-field field-copy-host">
              <span className="context-label">
                {field.label}
                <FieldCopyButton text={field.value} label={`Copy ${field.label}`} />
              </span>
              <span className="context-value content-text">{field.value}</span>
            </div>
          ))}
        </div>
      )}

      {/* Selection counter */}
      {showCheckboxes && requiredSelections && (
        <div className="selection-counter">
          <span className={selectedIds.size === requiredSelections ? 'counter-complete' : ''}>
            {selectedIds.size} / {requiredSelections} selected
          </span>
        </div>
      )}

      {/* Card grid */}
      <div className="card-grid">
        {cards.map((card, index) => (
          <ContentCard
            key={card.id}
            id={card.id}
            content={card.content}
            label={card.label || `${index + 1}`}
            selected={selectedIds.has(card.id)}
            onSelect={onSelect}
            onEdit={onEdit}
            onRegenerate={onRegenerate}
            onUndo={onUndo && undoableIds?.has(card.id) ? () => onUndo(card.id) : undefined}
            regenerating={regeneratingId === card.id}
            showCheckbox={showCheckboxes}
            compact={compact}
            stale={staleIds.has(card.id)}
          />
        ))}
      </div>
    </div>
  );
}
